import { create } from 'zustand';
import apiClient from '../lib/api';

// ---------------------------------------------------------------- types

export interface LeaveType {
  id: string;
  name: string;
  code: string;
  daysAllowed: number;
  paid: boolean;
  carryForward: boolean;
  maxCarryForward: number;
}

export interface LeaveRequest {
  id: string;
  employeeId: string;
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  reason?: string;
  rejectionReason?: string;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
  supportTicketId?: number;
  supportTicketNumber?: string;
  type: string;       // leave type name (joined from leave_types)
  typeCode: string;    // leave type code
  firstName: string;   // employee first name
  lastName: string;    // employee last name
  department?: string;
}

export interface LeaveBalance {
  leaveTypeId: string;
  type: string;
  code: string;
  total: number;
  used: number;
  remaining: number;
}

export interface TeamCalendarEntry {
  id: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  status: string;
  type: string;
  typeCode: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  department?: string;
}

export interface Holiday {
  id: string;
  name: string;
  date: string;
  type: string;
  description?: string;
}

export interface ApproverManager {
  id: string;
  name: string;
}

export interface MyApprover {
  employeeId: string | null;
  managers: ApproverManager[];    // all reporting managers (primary first)
  fallbackName: string | null;    // HR approver used when no manager is set
}

// --------------------------------------------------------------- normalization

function normalizeRequest(raw: any): LeaveRequest {
  return {
    id: raw.id,
    employeeId: raw.employee_id ?? raw.employeeId,
    leaveTypeId: raw.leave_type_id ?? raw.leaveTypeId,
    startDate: raw.start_date ?? raw.startDate,
    endDate: raw.end_date ?? raw.endDate,
    totalDays: Number(raw.total_days ?? raw.totalDays),
    status: raw.status,
    reason: raw.reason,
    rejectionReason: raw.rejection_reason ?? raw.rejectionReason,
    approvedBy: raw.approved_by ?? raw.approvedBy,
    approvedAt: raw.approved_at ?? raw.approvedAt,
    createdAt: raw.created_at ?? raw.createdAt,
    supportTicketId: raw.support_ticket_id ?? raw.supportTicketId,
    supportTicketNumber: raw.support_ticket_number ?? raw.supportTicketNumber,
    type: raw.type ?? '',
    typeCode: raw.type_code ?? raw.typeCode ?? '',
    firstName: raw.first_name ?? raw.firstName ?? '',
    lastName: raw.last_name ?? raw.lastName ?? '',
    department: raw.department,
  };
}

function normalizeBalance(raw: any): LeaveBalance {
  return {
    leaveTypeId: raw.leave_type_id ?? raw.leaveTypeId,
    type: raw.type,
    code: raw.code,
    total: Number(raw.total),
    used: Number(raw.used),
    remaining: Number(raw.remaining),
  };
}

function normalizeLeaveType(raw: any): LeaveType {
  return {
    id: raw.id,
    name: raw.name,
    code: raw.code,
    daysAllowed: Number(raw.days_allowed ?? raw.daysAllowed),
    paid: raw.paid,
    carryForward: raw.carry_forward ?? raw.carryForward,
    maxCarryForward: Number(raw.max_carry_forward ?? raw.maxCarryForward ?? 0),
  };
}

function normalizeCalendar(raw: any): TeamCalendarEntry {
  return {
    id: raw.id,
    startDate: raw.start_date ?? raw.startDate,
    endDate: raw.end_date ?? raw.endDate,
    totalDays: Number(raw.total_days ?? raw.totalDays),
    status: raw.status,
    type: raw.type,
    typeCode: raw.type_code ?? raw.typeCode ?? '',
    employeeId: raw.employee_id ?? raw.employeeId,
    firstName: raw.first_name ?? raw.firstName ?? '',
    lastName: raw.last_name ?? raw.lastName ?? '',
    department: raw.department,
  };
}

function normalizeHoliday(raw: any): Holiday {
  return {
    id: raw.id,
    name: raw.name,
    date: raw.date,
    type: raw.type,
    description: raw.description,
  };
}

// --------------------------------------------------------------- store

interface LeaveState {
  leaveRequests: LeaveRequest[];
  balances: LeaveBalance[];
  leaveTypes: LeaveType[];
  teamCalendar: TeamCalendarEntry[];
  holidays: Holiday[];
  myApprover: MyApprover | null;
  isLoading: boolean;
  error: string | null;

  fetchLeaveRequests: () => Promise<void>;
  fetchBalances: () => Promise<void>;
  fetchLeaveTypes: () => Promise<void>;
  fetchTeamCalendar: (month?: number, year?: number) => Promise<void>;
  fetchHolidays: () => Promise<void>;
  fetchMyApprover: () => Promise<void>;
  submitLeaveRequest: (data: any) => Promise<void>;
  cancelLeaveRequest: (id: string) => Promise<void>;
  approveLeaveRequest: (id: string) => Promise<void>;
  rejectLeaveRequest: (id: string, reason?: string) => Promise<void>;
}

export const useLeaveStore = create<LeaveState>((set, get) => ({
  leaveRequests: [],
  balances: [],
  leaveTypes: [],
  teamCalendar: [],
  holidays: [],
  myApprover: null,
  isLoading: false,
  error: null,

  fetchLeaveRequests: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/people/leave/requests');
      const raw = response.data?.data ?? [];
      set({ leaveRequests: raw.map(normalizeRequest), isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to fetch leave requests', isLoading: false });
    }
  },

  fetchBalances: async () => {
    try {
      const response = await apiClient.get('/people/leave/balances');
      const raw = response.data?.data ?? [];
      set({ balances: raw.map(normalizeBalance) });
    } catch (error: any) {
      console.error('Failed to fetch leave balances', error);
    }
  },

  fetchLeaveTypes: async () => {
    try {
      const response = await apiClient.get('/people/leave/types');
      const raw = response.data?.data ?? [];
      set({ leaveTypes: raw.map(normalizeLeaveType) });
    } catch (error: any) {
      console.error('Failed to fetch leave types', error);
    }
  },

  fetchTeamCalendar: async (month?: number, year?: number) => {
    try {
      const params: any = {};
      if (month) params.month = month;
      if (year) params.year = year;
      const response = await apiClient.get('/people/leave/team-calendar', { params });
      const raw = response.data?.data ?? [];
      set({ teamCalendar: raw.map(normalizeCalendar) });
    } catch (error: any) {
      console.error('Failed to fetch team calendar', error);
    }
  },

  fetchHolidays: async () => {
    try {
      const response = await apiClient.get('/people/holidays');
      const raw = response.data?.data ?? [];
      set({ holidays: raw.map(normalizeHoliday) });
    } catch (error: any) {
      console.error('Failed to fetch holidays', error);
    }
  },

  fetchMyApprover: async () => {
    try {
      const response = await apiClient.get('/people/reporting-managers/my-manager');
      const d = response.data?.data ?? response.data ?? {};
      const managers = Array.isArray(d.managers) ? d.managers : [];
      const fb = d.fallback;
      set({
        myApprover: {
          employeeId: d.employeeId ?? null,
          managers: managers
            .map((m: any) => ({ id: m.id, name: `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() }))
            .filter((m: any) => m.id && m.name),
          fallbackName: fb ? `${fb.first_name ?? ''} ${fb.last_name ?? ''}`.trim() : null,
        },
      });
    } catch (error: any) {
      console.error('Failed to fetch approver', error);
    }
  },

  submitLeaveRequest: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiClient.post('/people/leave/requests', data);
      const leaveId = res.data?.data?.id || res.data?.id || `leave-${Date.now()}`;
      
      // Auto-create linked Support Ticket in HR Queue
      try {
        const { useUnifiedSyncStore } = await import('./unifiedSyncStore');
        const lt = get().leaveTypes.find((t: LeaveType) => t.id === data.leaveTypeId);
        await useUnifiedSyncStore.getState().createUnifiedRequest({
          originModule: 'people',
          recordType: 'leave_request',
          title: `Leave Application: ${lt?.name || 'Leave'} (${data.totalDays} Days)`,
          description: `Applied for ${lt?.name || 'Leave'}: ${data.startDate} to ${data.endDate} (${data.totalDays} days).\nReason: ${data.reason}`,
          priority: 'medium',
          entryChannel: 'module',
          departmentQueue: 'hr',
          metadata: { leaveId, ...data },
        });
      } catch (e) {
        console.warn('Unified sync creation fallback for leave:', e);
      }

      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to submit leave request', isLoading: false });
      throw error;
    }
  },

  cancelLeaveRequest: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.delete(`/people/leave/requests/${id}`);
      try {
        const { useUnifiedSyncStore } = await import('./unifiedSyncStore');
        await useUnifiedSyncStore.getState().syncModuleActionToTicket({
          recordType: 'leave_request',
          recordId: id,
          action: 'cancel',
          moduleStatus: 'cancelled',
          note: 'Cancelled by requester in People hub',
        });
      } catch (e) {
        console.warn('Unified sync cancel fallback for leave:', e);
      }
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to cancel leave request', isLoading: false });
      throw error;
    }
  },

  approveLeaveRequest: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.patch(`/people/leave/requests/${id}/approve`);
      
      // Sync to linked ticket: resolve ticket
      try {
        const { useUnifiedSyncStore } = await import('./unifiedSyncStore');
        await useUnifiedSyncStore.getState().syncModuleActionToTicket({
          recordType: 'leave_request',
          recordId: id,
          action: 'approve',
          moduleStatus: 'approved',
          note: 'Approved by HR manager in People > Leave',
        });
      } catch (e) {
        console.warn('Unified sync approval fallback for leave:', e);
      }

      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to approve leave request', isLoading: false });
      throw error;
    }
  },

  rejectLeaveRequest: async (id: string, reason?: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.patch(`/people/leave/requests/${id}/reject`, { reason });

      // Sync to linked ticket: close ticket
      try {
        const { useUnifiedSyncStore } = await import('./unifiedSyncStore');
        await useUnifiedSyncStore.getState().syncModuleActionToTicket({
          recordType: 'leave_request',
          recordId: id,
          action: 'reject',
          moduleStatus: 'rejected',
          note: reason || 'Rejected by HR in People > Leave',
        });
      } catch (e) {
        console.warn('Unified sync rejection fallback for leave:', e);
      }

      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to reject leave request', isLoading: false });
      throw error;
    }
  },
}));
